import { compareCents, multiplyCents } from '../money.js';
import { addDays, localDate, nextLocalMidnight, weekdayOf, zonedInstant } from '../time/zoned.js';

/**
 * Campaign limits, cost and timing (M9, owner decisions 2026-10-14 #9, #17–#20, #34–#35). Pure.
 */

/** Largest audience a campaign may have (Phase 0 safety limit, #9). */
export const MAX_AUDIENCE = 10_000;

/** Campaign and sequence texts stop at this share of a number's daily limit; reps keep the rest (#17). */
export const CAMPAIGN_NUMBER_SHARE = 0.8;
/** Campaign and sequence texts stop at this share of the workspace's daily segment limit (#18). */
export const CAMPAIGN_BRAND_SHARE = 0.9;
/** The workspace's daily segment limit until an admin changes it (T-Mobile's unvetted brand cap). */
export const DEFAULT_DAILY_SEGMENT_LIMIT = 2000;

/** Most campaign texts a number may hand over in one Pacific day. */
export function campaignNumberDayLimit(maxPerDay: number): number {
  return Math.floor(maxPerDay * CAMPAIGN_NUMBER_SHARE);
}

/** Most campaign segments the workspace may hand over in one Pacific day. */
export function campaignBrandDayLimit(dailySegmentLimit: number): number {
  return Math.floor(dailySegmentLimit * CAMPAIGN_BRAND_SHARE);
}

/**
 * The estimate's price per segment (#34): Telnyx's message fee plus a carrier fee, both placeholders
 * labelled "estimate" until real prices replace them in M16. Cents, as decimal strings (money.ts).
 */
export const PRICE_PER_SEGMENT_CENTS = '0.4';
export const CARRIER_FEE_PER_SEGMENT_CENTS = '0.3';
export const ESTIMATE_PER_SEGMENT_CENTS = '0.7';

/** Estimated cost of `segments` segments, in cents (decimal string). */
export function estimateCostCents(segments: number): string {
  return multiplyCents(ESTIMATE_PER_SEGMENT_CENTS, segments);
}

/** A campaign above the workspace's per-campaign spending limit cannot launch (#35). */
export function withinSpendLimit(estimateCents: string, limitCents: string): boolean {
  return compareCents(estimateCents, limitCents) <= 0;
}

/** The default per-campaign spending limit: $100. */
export const DEFAULT_SPEND_LIMIT_CENTS = '10000';

/** Longest window a merchant must not have had another campaign text in (#8: admins can lower it). */
export const MAX_FREQUENCY_DAYS = 7;

export interface NumberCapacity {
  maxPerDay: number;
}

/**
 * Roughly how many sending days the campaign needs, from the numbers' daily limits and the workspace's
 * segment limit. For the review screen only ("about 2 sending days"); texts other than this campaign's,
 * sending hours and caps can make it longer.
 */
export function estimateSendingDays(
  texts: number,
  segments: number,
  numbers: readonly NumberCapacity[],
  dailySegmentLimit: number,
): number | null {
  if (texts === 0) return 0;
  const perDayTexts = numbers.reduce((sum, n) => sum + campaignNumberDayLimit(n.maxPerDay), 0);
  if (perDayTexts <= 0) return null;
  const avgSegments = Math.max(segments / texts, 1);
  const brandTexts = Math.floor(campaignBrandDayLimit(dailySegmentLimit) / avgSegments);
  const perDay = Math.min(perDayTexts, brandTexts);
  if (perDay <= 0) return null;
  return Math.ceil(texts / perDay);
}

/** Where campaign days are counted. Eastern opens first; Pacific midnight is after every US window. */
const COUNT_ZONE = 'America/New_York';
const END_ZONE = 'America/Los_Angeles';
/** Unsent campaign texts are dropped after this many sending days (#20). */
export const SENDING_DAYS_BEFORE_EXPIRY = 3;

/**
 * When a campaign's unsent texts expire (#20): the end of its third sending day. A sending day is a
 * Monday to Saturday that is not a federal holiday (observed or actual date), counted on Eastern
 * calendar dates. The launch day counts only when it starts before 5 PM Eastern on a weekday (noon on a
 * Saturday), so a few hours remain to send. The day ends at midnight Pacific, after every US campaign
 * window (Hawaii's 7 PM is 10 PM Pacific).
 */
export function campaignExpiresAt(start: Date, closedDates: ReadonlySet<string>): Date {
  const firstDate = localDate(COUNT_ZONE, start);
  let counted = 0;
  let date = firstDate;
  for (let i = 0; i < 60; i += 1) {
    const weekday = weekdayOf(date);
    const isSendingDay = weekday !== 0 && !closedDates.has(date);
    let counts = isSendingDay;
    if (counts && date === firstDate) {
      const cutoff = zonedInstant(COUNT_ZONE, date, weekday === 6 ? 12 * 60 : 17 * 60);
      counts = start.getTime() < cutoff.getTime();
    }
    if (counts) {
      counted += 1;
      if (counted === SENDING_DAYS_BEFORE_EXPIRY) {
        return nextLocalMidnight(END_ZONE, zonedInstant(COUNT_ZONE, date, 12 * 60));
      }
    }
    date = addDays(date, 1);
  }
  // Unreachable with real holiday data; never leave a campaign without an end.
  return nextLocalMidnight(END_ZONE, zonedInstant(COUNT_ZONE, date, 12 * 60));
}

/** Auto-pause thresholds (#25): judged once at least this many texts have an outcome. */
export const AUTO_PAUSE_MIN_TEXTS = 100;
export const AUTO_PAUSE_FAILURE_RATE = 0.15;
export const AUTO_PAUSE_OPT_OUT_RATE = 0.05;

export type AutoPauseReason = 'failures' | 'opt_outs';

/** Whether a running campaign should pause itself, from its counts so far. */
export function autoPauseReason(counts: {
  handedOver: number;
  failed: number;
  optedOut: number;
}): AutoPauseReason | null {
  if (counts.handedOver < AUTO_PAUSE_MIN_TEXTS) return null;
  if (counts.failed / counts.handedOver > AUTO_PAUSE_FAILURE_RATE) return 'failures';
  if (counts.optedOut / counts.handedOver >= AUTO_PAUSE_OPT_OUT_RATE) return 'opt_outs';
  return null;
}

/** Opt-outs within this long after a campaign text count against the campaign (#32). */
export const OPT_OUT_ATTRIBUTION_DAYS = 7;
/** Test sends allowed per campaign per Pacific day (#29). */
export const TEST_SENDS_PER_DAY = 5;
